import { observer } from 'mobx-react';
import { FC, useEffect, useRef, useState } from 'react';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { Helmet } from 'react-helmet-async';
import { Outlet, useSearchParams } from 'react-router-dom';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import styles from '../../../styles/pages.module.scss';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { useInventoryHeaders } from '../../../modules/hooks/theaders/inventory.headers';
import { INVENTORY_DEFAULT_SORT, useInventoryList } from '../../../modules/hooks/apis/use-inventory-list';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IMFPPStore } from '../../../modules/stores';
import { DispatchLogsTypes } from '@monorepo/controlled/src/hooks/use-logs';
import dayjs from 'dayjs';
import { useInventoryActions } from '../../../modules/hooks/actions/inventory.actions';
import { sendGtagEvent } from '@monorepo/tools/src/lib/tools/tracking';
import { TrackingActions } from '@monorepo/tools/src/lib/consts/tracking/actions';
import { EVENTS } from '@monorepo/tools/src/lib/consts/tracking/events';
import { InventoryModel, IInventory } from '../../../modules/models/inventory.model';
import { GenerateLinkModal } from '../../../modules/components/modals/generate-link-modal';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
// todo [aviva] why do we need this ?
function csvEscape(value: unknown): string {
	if (value === null || value === undefined) {
		return '';
	}
	const s = String(value);
	if (/[",\r\n]/.test(s)) {
		return `"${s.replace(/"/g, '""')}"`;
	}
	return s;
}

export const Inventory: FC = observer(() => {
	const { currentRouteWithoutSpecialChars } = useRoute();
	const { toastsStore, publisherStore, inventoryStore, modalsStore } = useStores<IMFPPStore>();
	const [searchParams] = useSearchParams();
	const accountId = searchParams.get('accountId');
	const { isLoading, error, data: inventoryData, total, hasMore, gotoPage } = useInventoryList();
	const [isLoadingCsv, setIsLoadingCsv] = useState<boolean>(false);
	const lastTablePageIndexRef = useRef(0);

	useEffect(() => {
		lastTablePageIndexRef.current = 0;
	}, [accountId]);

	const handleGenerateLink = (row: IInventory) => {
		if (!accountId) {
			return;
		}
		modalsStore.addModal({
			children: <GenerateLinkModal inventory={row} accountId={accountId} />,
		});
	};

	const { columns } = useInventoryHeaders({ onGenerateLink: handleGenerateLink });
	const { FiltersActions } = useInventoryActions();
	const onDownloadInventoryFile = () => {
		if (accountId) {
			const account = publisherStore
				.getListStore()
				.getData()
				?.find(account => accountId.toString() === account.id.toString());
			const fileName = `Manifesto inventory - ${account?.name || accountId} - ${dayjs().format('YYYY/MM/DD')}`;
			const sortedRows = inventoryStore.getSortedRows();
			if (sortedRows.length <= 0) {
				toastsStore.addToast({
					msg: 'Cannot export empty file.',
					type: DispatchLogsTypes.Info,
				});
				return;
			}
			setIsLoadingCsv(true);
			const csvColumns: (keyof InventoryModel)[] = ['domain', 'geo', 'vertical', 'deepLink', 'commission', 'clickCap'];
			const headers = csvColumns.join(',');
			const rows = sortedRows.map(row => csvColumns.map(col => csvEscape(row.original[col])).join(',')).join('\n');
			const csvContent = `${headers}\n${rows}`;
			const blob = new Blob([csvContent], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `${fileName}.csv`);
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
			setIsLoadingCsv(false);
		}
	};

	return (
		<Page unstyled={true} className={styles.article}>
			<Helmet>
				<title>Manifesto - Inventory</title>
			</Helmet>
			<Pagebar classes={{ pagebar: styles.padding }} debugProps={{ dataAttrs: [new DataAttribute('id', 'pagebar')] }}>
				<PagebarTitle>Inventory</PagebarTitle>
			</Pagebar>
			<ControlledTable
				serverBackedTable
				defaultSortBy={INVENTORY_DEFAULT_SORT}
				onExportToCsv={() => {
					sendGtagEvent({
						action: TrackingActions.Click,
						category: EVENTS.CLICK.INDEX_PAGE.ACTION_LINER.EXPORT,
						label: 'Export Inventory CSV',
						value: window.location.href,
					});
					onDownloadInventoryFile();
				}}
				isLoadingCsv={isLoadingCsv}
				currentRoute={currentRouteWithoutSpecialChars}
				classes={{ wrapper: styles.padding }}
				label={'Inventory'}
				debugProps={{ dataAttrs: [new DataAttribute('id', 'inventory_table')] }}
				columns={columns}
				data={inventoryData || []}
				isLoading={isLoading}
				isError={Boolean(error)}
				totalRows={total || undefined}
				onPageChange={({ pageIndex, pageSize }) => {
					const prevIndex = lastTablePageIndexRef.current;
					lastTablePageIndexRef.current = pageIndex;
					if (pageIndex <= prevIndex) {
						return;
					}
					const loadedCount = inventoryData?.length ?? 0;
					if (hasMore && loadedCount > 0 && pageIndex > 0 && pageIndex * pageSize >= loadedCount) {
						gotoPage(inventoryStore.page + 1);
					}
				}}>
				{({ sortedRows }) => {
					inventoryStore.setSortedRows(sortedRows);
					return {
						FiltersActions,
					};
				}}
			</ControlledTable>
			<Outlet />
		</Page>
	);
});
